import React from "react";
import { motion } from "framer-motion";
import { User, Camera, Upload, MapPin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { DoulaListing, UserInfo } from "./types";

interface PersonalInformationCardProps {
	listing: DoulaListing | null;
	userInfo: UserInfo | null;
	uploadingPhoto: boolean;
	onPhotoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onUserInfoChange: (field: keyof UserInfo, value: string) => void;
	onListingChange: (field: keyof DoulaListing, value: string) => void;
}

export default function PersonalInformationCard({
	listing,
	userInfo,
	uploadingPhoto,
	onPhotoUpload,
	onUserInfoChange,
	onListingChange,
}: PersonalInformationCardProps) {
	return (
		<motion.div
			initial={{ opacity: 0, x: -20 }}
			animate={{ opacity: 1, x: 0 }}
			transition={{ delay: 0.1 }}
		>
			<Card className="glass-card border-0 shadow-lg">
				<CardHeader>
					<CardTitle className="text-xl font-karla flex items-center">
						<User className="h-5 w-5 mr-2 text-newTheme-orange" />
						Personal Information
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					{/* Profile Photo */}
					<div>
						<Label className="text-sm font-medium text-black font-karla">
							Profile Photo
						</Label>
						<div className="flex items-center space-x-4 mt-2">
							<div className="relative">
								{listing?.profile_photo_url ? (
									<img
										src={listing.profile_photo_url}
										alt="Profile"
										className="w-20 h-20 rounded-full object-cover"
										onError={(e) => {
											console.error(
												"Failed to load profile image:",
												e.currentTarget.src,
											);
											console.error("Image error event:", e);
											// Hide the broken image and show placeholder
											e.currentTarget.style.display = "none";
										}}
										onLoad={() => { }}
									/>
								) : null}
								<div
									className="w-20 h-20 rounded-full bg-newTheme-lightBlue flex items-center justify-center border-2 border-newTheme-lightBlue"
									style={{
										display: listing?.profile_photo_url ? "none" : "flex",
									}}
								>
									<Camera className="h-8 w-8 text-newTheme-orange" />
								</div>
								<label className="absolute bottom-0 right-0 bg-newTheme-spruce text-newTheme-cotton p-1.5 rounded-full cursor-pointer">
									<Upload className="h-3 w-3" />
									<input
										type="file"
										accept="image/*"
										onChange={onPhotoUpload}
										className="hidden"
									/>
								</label>
							</div>
							<div>
								<p className="text-sm text-gray-600 font-karla">
									{uploadingPhoto
										? "Uploading..."
										: "Upload a professional photo"}
								</p>
							</div>
						</div>
					</div>

					{/* Basic Info */}
					<div className="grid grid-cols-2 gap-4">
						<div>
							<Label
								htmlFor="fullName"
								className="text-sm font-medium text-gray-700 font-karla"
							>
								Full Name
							</Label>
							<Input
								id="fullName"
								value={userInfo?.full_name || ""}
								onChange={(e) => onUserInfoChange("full_name", e.target.value)}
								className="mt-1"
							/>
						</div>
						<div>
							<Label
								htmlFor="email"
								className="text-sm font-medium text-gray-700 font-karla"
							>
								Email
							</Label>
							<Input
								id="email"
								type="email"
								value={userInfo?.email || ""}
								disabled
								className="mt-1"
							/>
						</div>
					</div>

					{/* Location */}
					<div>
						<Label className="text-sm font-medium text-black font-karla flex items-center">
							<MapPin className="h-4 w-4 mr-1 text-newTheme-orange" />
							Location
						</Label>
						<div className="grid grid-cols-2 gap-4 mt-2">
							<Input
								placeholder="Address"
								value={listing?.location_address || ""}
								onChange={(e) =>
									onListingChange("location_address", e.target.value)
								}
							/>
							<Input
								placeholder="City"
								value={listing?.location_city || ""}
								onChange={(e) =>
									onListingChange("location_city", e.target.value)
								}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4 mt-2">
							<Input
								placeholder="State"
								value={listing?.location_state || ""}
								onChange={(e) =>
									onListingChange("location_state", e.target.value)
								}
							/>
							<Input
								placeholder="ZIP Code"
								type="number"
								value={listing?.location_zip || ""}
								onChange={(e) =>
									onListingChange("location_zip", e.target.value)
								}
							/>
						</div>
					</div>
				</CardContent>
			</Card>
		</motion.div>
	);
}
